import React, { useEffect, useRef, useState } from 'react';
import { milestoneBurst } from '@radevents/shared';

/**
 * Bewegung der Winterchallenge: hochzählende Zahlen, ein Balken, der sich füllt. Unter
 * `prefers-reduced-motion` stehen die Werte sofort da.
 *
 * Eine animierte Illustration (Fahrer als zerschnittene Rasterebenen, Schneefall) gab es am
 * 23.10.2026 kurz und flog wieder raus: aus einem Pixelbild ausgeschnittene Ebenen haben Lücken an
 * den Kanten und wirken zittrig. Wer das wieder will, braucht die Fahrer als getrennte Vektoren.
 */

const ART = '/bilder/winterchallenge/fahrer.webp';
/** Dieselben Fahrer ohne weißen Grund (Farbe zu Alpha gegen Weiß, 420 px breit), für getönte Flächen. */
const ART_FREE = '/bilder/winterchallenge/fahrer-frei.webp';

export function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

/** Romans Illustration, ruhig. In einer Hülle, damit kleine und große Fassung dieselbe Regel haben. */
export function ChallengeArt({ className, free = false }: { className?: string; free?: boolean }) {
  return (
    <div className={`wc-art${className ? ` ${className}` : ''}`} aria-hidden>
      <img src={free ? ART_FREE : ART} alt="" width={1000} height={606} />
    </div>
  );
}

/** Zählt beim ersten Erscheinen von 0 (bzw. vom alten Wert) auf `value` hoch. */
export function useCountUp(value: number, ms = 1100): number {
  const reduced = usePrefersReducedMotion();
  const [shown, setShown] = useState(reduced ? value : 0);
  const from = useRef(0);
  useEffect(() => {
    if (reduced) { setShown(value); return; }
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(a + (value - a) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
      else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, ms, reduced]);
  return shown;
}

/** Breite eines Balkens: erst 0, dann der Wert — die CSS-Übergang füllt ihn sichtbar. */
export function useGrow(percent: number): number {
  const [w, setW] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setW(percent));
    return () => cancelAnimationFrame(id);
  }, [percent]);
  return w;
}

/**
 * Eine hochzählende Zahl, die von Anfang an so breit ist wie ihr Endwert. Ohne das stand eine
 * kurze Zahl erst neben ihrem Nachbarn und rutschte beim Hochzählen in die nächste Zeile; die
 * Seite sprang (Phil, 25.10.2026). Der Endwert liegt unsichtbar darunter und gibt die Breite vor,
 * Vorleser lesen nur ihn.
 */
export function CountUp({ value, format, ms }: { value: number; format: (n: number) => string; ms?: number }) {
  const shown = useCountUp(value, ms);
  return (
    <span className="wc-countup">
      <span className="sr-only">{format(value)}</span>
      <span className="wc-countup-final" aria-hidden>{format(value)}</span>
      <span className="wc-countup-live" aria-hidden>{format(shown)}</span>
    </span>
  );
}

/**
 * Meilenstein erreicht: ein Ring läuft aus, Konfetti fliegt aus der Mitte und fällt. Einmal, beim
 * Erscheinen, kein Dauerfeuerwerk. Liegt über dem, was es feiert (`.wc-burst-host` um Pokal oder
 * Punkt), und fängt keine Klicks. Unter `prefers-reduced-motion` gar nicht.
 */
export function MilestoneBurst({ radius = 74, count = 18 }: { radius?: number; count?: number }) {
  const reduced = usePrefersReducedMotion();
  if (reduced) return null;
  return (
    <span className="wc-burst" aria-hidden>
      <span className="wc-burst-ring" />
      {milestoneBurst(count, radius).map((p, i) => (
        <span key={i} className={`wc-burst-piece wc-burst-piece--${p.shape}`} style={{
          ['--dx' as string]: `${p.dx}px`, ['--dy' as string]: `${p.dy}px`, ['--rot' as string]: `${p.rotate}deg`,
          width: p.shape === 'bar' ? p.size * 0.55 : p.size, height: p.size,
          background: p.color, animationDelay: `${120 + p.delay}ms`,
        } as React.CSSProperties} />
      ))}
    </span>
  );
}

/**
 * Der Meilenstein, den `km` gerade überquert hat, solange die Komponente steht. Beim ersten
 * Zeichnen nie: wer die Seite mit 1.042 km öffnet, hat nichts Neues geschafft.
 */
export function useFreshMilestone(km: number, crossed: (before: number, after: number) => number | null): number | null {
  const previous = useRef(km);
  const [fresh, setFresh] = useState<number | null>(null);
  useEffect(() => {
    if (km !== previous.current) setFresh(km > previous.current ? crossed(previous.current, km) : null);
    previous.current = km;
  }, [km, crossed]);
  return fresh;
}
